import { RotateCcw, X } from 'lucide-react';
import clsx from 'clsx';
import { useTutorial } from './TutorialContext';
import { getChapterDisplayStatus } from './useTutorialApi';

const STATUS_LABELS = {
  not_started: { label: 'Not started', className: 'bg-gray-100 text-gray-600' },
  in_progress: { label: 'In progress', className: 'bg-amber-100 text-amber-800' },
  completed: { label: 'Completed', className: 'bg-green-100 text-green-800' },
  updated: { label: 'Updated', className: 'bg-primary-100 text-primary-800' },
};

export default function TutorialHub() {
  const {
    hubOpen,
    closeHub,
    chapters,
    progressByChapter,
    startChapter,
    restartChapter,
    resetAll,
  } = useTutorial();

  if (!hubOpen) return null;

  const handleStartOrContinue = (chapter) => {
    const progress = progressByChapter[chapter.id];
    const status = getChapterDisplayStatus(chapter, progress);

    if (status === 'in_progress' && progress) {
      startChapter(chapter.id, progress.lastStepIndex ?? 0);
      return;
    }

    startChapter(chapter.id, 0);
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center pt-[12vh] px-4">
      <button
        type="button"
        className="absolute inset-0 bg-gray-900/40"
        aria-label="Close tutorial hub"
        onClick={closeHub}
      />

      <div className="relative w-full max-w-xl bg-surface rounded-lg border border-surface-border shadow-xl overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-surface-border">
          <div>
            <h2 className="text-sm font-semibold text-gray-900">Tutorial Hub</h2>
            <p className="text-2xs text-gray-500 mt-0.5">
              Learn the Hub in short chapters. Restart any chapter anytime.
            </p>
          </div>
          <button
            type="button"
            onClick={closeHub}
            className="p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-surface-hover"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="max-h-[60vh] overflow-y-auto p-2 space-y-1">
          {chapters.map((chapter) => {
            const progress = progressByChapter[chapter.id];
            const status = getChapterDisplayStatus(chapter, progress);
            const statusMeta = STATUS_LABELS[status];
            const Icon = chapter.icon;
            const actionLabel = status === 'in_progress' ? 'Continue' : status === 'completed' || status === 'updated' ? 'Watch again' : 'Start';

            return (
              <div
                key={chapter.id}
                className="flex items-start gap-3 p-3 rounded-md hover:bg-surface-hover border border-transparent hover:border-surface-border"
              >
                <div className="mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md bg-primary-50 text-primary-700">
                  <Icon className="h-4 w-4" />
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="text-sm font-medium text-gray-900">{chapter.title}</h3>
                    <span className={clsx('text-2xs font-medium px-1.5 py-0.5 rounded', statusMeta.className)}>
                      {statusMeta.label}
                    </span>
                    <span className="text-2xs text-gray-400 tabular-nums">
                      {chapter.steps.length} steps · v{chapter.version}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5">{chapter.description}</p>

                  <div className="flex items-center gap-2 mt-2">
                    <button
                      type="button"
                      onClick={() => handleStartOrContinue(chapter)}
                      className="px-2.5 py-1 text-xs font-medium rounded-md bg-primary-600 text-white hover:bg-primary-700"
                    >
                      {actionLabel}
                    </button>
                    {progress && (
                      <button
                        type="button"
                        onClick={() => restartChapter(chapter.id)}
                        className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-gray-600 border border-surface-border rounded-md hover:bg-surface-secondary"
                      >
                        <RotateCcw className="h-3 w-3" />
                        Restart
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="px-4 py-3 border-t border-surface-border bg-surface-secondary/50 flex items-center justify-between">
          <p className="text-2xs text-gray-500">
            New features bump chapter versions so you know when to rewatch.
          </p>
          <button
            type="button"
            onClick={() => resetAll()}
            className="text-2xs font-medium text-gray-500 hover:text-gray-700 underline"
          >
            Reset all progress
          </button>
        </div>
      </div>
    </div>
  );
}
